import { Award, Handshake, HandshakeIcon, Star, Trophy } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import MyMedalsMobile from './mobile';

import IcBoot from '@/assets/ic_boot.svg';
import {
  Button,
  Container,
  InputSearch,
  Loader,
  Modal,
  NavBar,
} from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import { Paginator } from '@/components/Paginator';
import { TshirtTeam } from '@/components/TshirtTeam';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { usePermutive } from '@/hooks/usePermutive';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import {
  GetTop50Medals,
  initMedallero,
  searchMedallero,
} from '@/services/medals';
import { SelectedScreen } from '@/types/editProfile';
import { ResponseInitMedals, ResponseTop50Medals } from '@/types/medals';
import { formatNumberToLocale, letterGenderById } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

const Icons = {
  top: <Trophy className="text-gdt-status-orange" size={20} />,
  challenge: <Handshake className="text-gdt-blue" size={20} />,
  premium: <Star className="text-gdt-status-yellow" size={20} />,
  award: <Award className="text-gdt-blue" size={20} />,
  f5: <img alt="f5" className="w-5 h-5" src={IcBoot} />,
  handshake: <HandshakeIcon className="text-gdt-blue" size={20} />,
};

interface MedalsData {
  icon: JSX.Element;
  iconBgColor: string;
  title: string;
  date: string;
  medals: number;
  torneo?: string;
}

interface Top50MedalsData {
  user: string;
  location: string;
  team: string;
  medals: number;
  index: number;
}

interface MedalsProps {
  contentCurrentPage: MedalsData[];
  allMedals: MedalsData[];
  handleContentCurrentPage: (medals: MedalsData[]) => void;
}

const Medals = ({
  contentCurrentPage,
  allMedals,
  handleContentCurrentPage,
}: MedalsProps) => {
  if (contentCurrentPage.length === 0) {
    return (
      <div className="bg-white rounded-lg py-10 justify-center items-center flex flex-col">
        <p className="text-lg font-semibold text-gdt-gray-2">
          Empezás a sumar medallas en la primera fecha de Gran DT.
        </p>
        <Link
          className="text-gdt-blue font-bold text-lg underline cursor-pointer"
          id="linkFaq"
          rel="noopener noreferrer"
          target="_blank"
          to={`${ROUTES.preguntasFrecuentes}?selectedScreen=TeamBuildingAndAwards&accordionId=juego-como-sumo-medallas`}
        >
          Consultá qué logros tienen recompensa
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg">
      {contentCurrentPage.map((medal, index) => (
        <div
          key={index}
          className="flex justify-between items-center border-b border-gdt-gray-5 py-5 px-10"
        >
          <div className="flex gap-5">
            <div
              className={`${medal.iconBgColor} rounded-full h-11 w-11 flex items-center justify-center`}
            >
              {medal.icon}
            </div>
            <div className="flex flex-col items-start">
              <span className="font-bold text-base">
                {medal.title}
                {medal.torneo ? ` de ${medal.torneo}` : ''}
              </span>
              <span className="text-sm font-semibold text-gdt-gray-4">
                {medal.date}
              </span>
            </div>
          </div>
          <div className="flex items-center mx-8">
            <span className="text-base font-semibold text-gdt-gray-2">
              {medal.torneo ? medal.medals : '--'}
            </span>
          </div>
        </div>
      ))}
      <Paginator
        className="flex px-28"
        currentContentPage={handleContentCurrentPage}
        items={allMedals}
        itemsPerPage={5}
      />
    </div>
  );
};

const ModalTitle = ({ title }: { title: string }) => (
  <div className="flex items-center justify-center gap-2 mb-10">
    <div className="bg-gdt-status-yellow-light w-8 h-8 rounded-full flex justify-center items-center">
      <Award className="text-gdt-status-yellow" size={20} />
    </div>
    <span className="text-lg text-gdt-blue">{title}</span>
  </div>
);

const MyMedals = () => {
  const [allMedals, setAllMedals] = useState<MedalsData[]>([]);
  const [paginatorTop50Key, setPaginatorTop50Key] = useState(0);
  const [contentCurrentPage, setContentCurrentPage] = useState<MedalsData[]>(
    []
  );
  const [contentCurrentPageTop50, setContentCurrentPageTop50] = useState<
    Top50MedalsData[]
  >([]);
  const [allMedalsTop50, setAllMedalsTop50] = useState<Top50MedalsData[]>([]);
  const [medalsTop50Backup, setMedalsTop50Backup] = useState<Top50MedalsData[]>(
    []
  );
  const [showModalAward, setShowModalAward] = useState(false);
  const [userData, setUserData] = useState({
    posicion: 0,
    nombreEquipo: '',
    nombreUsuario: '',
    cantidad: 0,
    rankingPublicado: 0,
  });
  const [showModalMedals, setShowModalMedals] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const { isDesktop } = useIsDesktop();
  const navigate = useNavigate();
  const { userInitialData } = useAuth();
  // const userInitialDataChange=useAtomValue(userDataAtom);

  const { data: dataTshirtShield } = useTshirtAndShield();

  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.medallero });
  useMetaData({
    title: 'Medallero',
  });

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.medallero,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_medallas: userData.cantidad,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  const iconResolver = ({
    type,
    tourney,
    msg,
  }: {
    type?: string;
    tourney?: string;
    msg?: string;
  }) => {
    if (
      tourney?.includes('Trivia') ||
      type?.includes('Trivia') ||
      msg?.includes('Trivia')
    ) {
      return {
        icon: Icons.award,
        iconBgColor: 'bg-gdt-gray-6',
      };
    }
    if (
      tourney?.includes('Premium') ||
      type?.includes('Premium') ||
      msg?.includes('Premium')
    ) {
      return {
        icon: Icons.premium,
        iconBgColor: 'bg-gdt-status-yellow-light',
      };
    }
    if (
      tourney?.includes('Primera División') ||
      type?.includes('Primera División') ||
      msg?.includes('Primera División')
    ) {
      return {
        icon: Icons.top,
        iconBgColor: 'bg-gdt-status-orange-light',
      };
    }
    if (
      tourney?.includes('Fútbol 5') ||
      type?.includes('Fútbol 5') ||
      msg?.includes('Fútbol 5')
    ) {
      return {
        icon: Icons.f5,
        iconBgColor: 'bg-gdt-gray-6',
      };
    }

    if (
      tourney?.includes('Desafios') ||
      type?.includes('Desafios') ||
      msg?.includes('Desafios')
    ) {
      return {
        icon: Icons.handshake,
        iconBgColor: 'bg-gdt-status-blue-light',
      };
    }
  };

  const { isLoading } = useQuery('initMedallero', async () => initMedallero(), {
    onSuccess(data) {
      const medals = data.medallero?.objectList?.map(
        (medal: ResponseInitMedals['medallero']['objectList'][0]) => ({
          icon:
            iconResolver({
              type: medal.tipoMedalla,
              tourney: medal.torneo,
              msg: medal.txtMensaje,
            })?.icon ?? Icons.top,
          iconBgColor:
            iconResolver({
              type: medal.tipoMedalla ?? medal.txtMensaje,
              tourney: medal.torneo,
              msg: medal.txtMensaje,
            })?.iconBgColor ?? 'bg-gdt-status-orange-light',
          title:
            medal.tipoMedalla ??
            medal.torneo ??
            medal.txtMessage ??
            medal.txtMensaje,
          date: medal.fecha,
          medals: medal.cantidadMedallas,
          torneo: medal.torneo,
        })
      );

      if (medals) {
        setAllMedals(medals);
        setContentCurrentPage(medals.slice(0, 10));
      }

      setUserData({
        posicion: data.posicion ?? 0,
        nombreUsuario: data.nombreUsuario,
        cantidad: data.cantidad,
        nombreEquipo: userInitialData?.nombre_equipo ?? '',
        rankingPublicado: data.rankingPublicado ?? 0,
      });

      if (data.isExportado) {
        GetTop50Medals(String(data.rankingPublicado)).then(async (data) => {
          const top = await data;
          const top50 = top.map((user: any) => ({
            user: `${user.nombre} ${user.apellido}`,
            location: user.provincia,
            team: user.nombreEquipo,
            medals: user.cantidadMedallas,
            index: user.posicion,
          }));
          setAllMedalsTop50(top50);
          setMedalsTop50Backup(top50);
          setContentCurrentPageTop50(top50.slice(0, 10));
        });
      }
    },
  });

  const handleContentCurrentPage = useCallback((medals: MedalsData[]) => {
    setContentCurrentPage(medals);
  }, []);

  const handleContentCurrentPageTop50 = useCallback(
    (top: Top50MedalsData[]) => {
      setContentCurrentPageTop50(top);
    },
    []
  );

  const handleSearchUser = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // hasta 30 caracteres, solo letras y espacios
    if (e.target.value.length > 30) {
      return;
    }
    const rawValue = e.target.value;
    const lettersOnly = rawValue
      .replace(/[^\-' A-Za-záéíóúÁÉÍÓÚñÑüÜöÖëËäÄïÏãªç`àèìòùÀÈÌÒÙ.´\\(\\)]/g, '')
      .toLowerCase();

    setSearchUser(lettersOnly);

    if (lettersOnly === '') {
      setAllMedalsTop50(medalsTop50Backup);
      setContentCurrentPageTop50(medalsTop50Backup.slice(0, 10));
      setPaginatorTop50Key((prevKey) => prevKey + 1);
      return;
    }

    if (lettersOnly !== '' && lettersOnly.length < 3) {
      setAllMedalsTop50([]); // Limpia los resultados si la búsqueda es inválida
      setContentCurrentPageTop50([]);
      return;
    }

    try {
      const results = await searchMedallero(lettersOnly);
      const formattedResults = results.usuarios.map(
        (user: ResponseTop50Medals) => ({
          user: `${user.nombre} ${user.apellido}`,
          location: user.provincia ?? '',
          team: user.nombreEquipo,
          medals: user.cantidadMedallas,
          index: user.posicion,
        })
      );
      setAllMedalsTop50(formattedResults);
      setContentCurrentPageTop50(formattedResults.slice(0, 10));
      setPaginatorTop50Key((prevKey) => prevKey + 1);
    } catch (error) {
      console.error('Error searching medallero:', error);
      setAllMedalsTop50([]);
      setContentCurrentPageTop50([]);
      setPaginatorTop50Key((prevKey) => prevKey + 1);
    }
  };

  if (!isDesktop) {
    return <MyMedalsMobile />;
  }

  const firstDate = userInitialData?.fechas_disponibles_obj?.[0]?.nombre;

  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Mis Medallas',
        },
      }}
    >
      <Container variant="default">
        <div className="flex justify-center mt-4 mx-6 ">
          <AdSlot divId={ADS_DIV_ID.horizontal} variant="HORIZONTAL" />
        </div>
        <div>
          <div className="m-6 md:m-0">
            <h1 className="text-2xl mb-3 mt-5 max-md:hidden">Mis Medallas</h1>
            <p className="font-medium lg:font-semibold text-sm text-gdt-gray-2">
              Cuanto mejor sea tu rendimiento, más medallas ganás y más premios
              te llevás.{' '}
            </p>
          </div>
          <div className="grid grid-cols-12 gap-8">
            <div className="mt-6 col-span-8">
              <div className="flex justify-between px-10 mb-3">
                <span className="font-semibold text-gdt-gray-4">
                  Tus logros
                </span>
                <span className="font-semibold text-gdt-gray-4">Medallas</span>
              </div>
              {isLoading ? (
                <Loader />
              ) : (
                <Medals
                  allMedals={allMedals}
                  contentCurrentPage={contentCurrentPage}
                  handleContentCurrentPage={handleContentCurrentPage}
                />
              )}
              <div className="flex items-center mb-3 mt-16 gap-3">
                <div className="rounded-full h-8 w-8 flex items-center justify-center bg-gdt-status-yellow-light">
                  <Award className="text-gdt-status-yellow" size={20} />
                </div>
                <h1 className="text-2xl">Top 50 Medallero</h1>
              </div>
              <div className="flex justify-between px-10 mb-3 font-semibold text-gdt-gray-4">
                <div className="flex gap-5">
                  <span>Posición</span>
                  <span>DT</span>
                </div>
                <div className="flex gap-56">
                  <span>Equipo</span>
                  <span>Medallas</span>
                </div>
              </div>
              <div className="bg-white rounded-lg">
                <div className="px-10 py-7">
                  <InputSearch
                    handleCancel={() => {
                      setSearchUser('');
                      setAllMedalsTop50(medalsTop50Backup);
                      setContentCurrentPageTop50(
                        medalsTop50Backup.slice(0, 10)
                      );
                      setPaginatorTop50Key((prevKey) => prevKey + 1);
                    }}
                    name="searchQuery"
                    placeholder="Buscar DT por nombre y apellido"
                    value={searchUser}
                    onChange={handleSearchUser}
                  />
                </div>
                {searchUser.length > 0 && allMedalsTop50.length === 0 && (
                  <div className="flex justify-center items-center h-40">
                    <span className="text-lg font-semibold text-gdt-gray-2">
                      No se han encontrado resultados
                    </span>
                  </div>
                )}
                {allMedalsTop50.length === 0 && searchUser.length === 0 && (
                  <div className="flex justify-center items-center h-40">
                    {userData.rankingPublicado === 0 ? (
                      <span className="text-lg font-semibold text-gdt-gray-2">
                        Consultá el ranking a partir de la{' '}
                        {firstDate || userInitialData?.fechaActiva.nombre}
                      </span>
                    ) : (
                      <span className="text-lg font-semibold text-gdt-gray-2">
                        Muy pronto podrás conocer el Top 50 del Medallero
                      </span>
                    )}
                  </div>
                )}
                {contentCurrentPageTop50.map((user) => (
                  <div
                    key={user.index}
                    className="flex justify-between items-center border-b border-gdt-gray-5 py-5 px-10"
                  >
                    <div className="flex gap-5">
                      <div
                        className={`rounded-full h-11 w-11 flex items-center font-bold justify-center ${
                          allMedalsTop50[0].user === user.user
                            ? 'bg-gdt-blue text-white'
                            : 'bg-gdt-blue-light2 text-gdt-blue'
                        }`}
                      >
                        {formatNumberToLocale(user.index)}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="font-bold text-base">{user.user}</span>
                        <span className="text-sm font-semibold text-gdt-gray-4">
                          {user.location}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-8 w-96">
                      <span className="text-base col-span-3 font-semibold text-gdt-gray-2 flex justify-start">
                        {user.team}
                      </span>
                      <span className="text-base col-span-1 font-semibold text-gdt-gray-2 flex justify-end">
                        {user.medals}
                      </span>
                    </div>
                  </div>
                ))}
                {allMedalsTop50.length > 0 && (
                  <Paginator
                    key={paginatorTop50Key}
                    breakLabel=""
                    className="flex px-28"
                    currentContentPage={handleContentCurrentPageTop50}
                    items={allMedalsTop50}
                    itemsPerPage={5}
                  />
                )}
              </div>
            </div>
            <div className="col-span-4 flex flex-col">
              <div className="bg-white mt-14 rounded-lg p-6 flex gap-12">
                <div className="w-28">
                  <TshirtTeam
                    shield={dataTshirtShield?.shield}
                    tshirt={dataTshirtShield?.tshirt}
                    withShield={true}
                  />
                </div>
                <div className="flex flex-col gap-y-2">
                  <span className="text-lg font-bold mt-4">
                    {userData.nombreEquipo}
                  </span>
                  <span className="text-base font-medium text-gdt-gray-3">
                    DT {userData.nombreUsuario}
                  </span>
                  <div className="border-b border-gdt-gray-5" />
                  <div className="flex gap-x-3">
                    <span className="text-base font-semibold text-gdt-gray-2">
                      Mis medallas
                    </span>
                    <span className="text-xl text-gdt-blue">
                      {formatNumberToLocale(userData.cantidad)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white mt-6 rounded-lg flex px-6 py-4 gap-4 items-center">
                <div className="flex items-center gap-2">
                  <div className="bg-gdt-status-yellow-light w-11 h-11 rounded-full flex justify-center items-center">
                    <Award className="text-gdt-status-yellow" size={20} />
                  </div>
                  <span className="text-lg text-gdt-gray-2">
                    TU POSICIÓN EN EL MEDALLERO
                  </span>
                </div>
                <span className="font-bold text-xl text-black font-lining-nums">
                  {[0, '-'].includes(userData.posicion)
                    ? '-'
                    : formatNumberToLocale(userData.posicion)}
                </span>
              </div>

              <div className="flex justify-center gap-10 mt-4">
                <Button
                  className="font-bold"
                  type="button"
                  variant="text"
                  onClick={() => setShowModalMedals(true)}
                >
                  ¿Cómo sumo medallas?
                </Button>
                <Button
                  className="font-bold"
                  type="button"
                  variant="text"
                  onClick={() => setShowModalAward(true)}
                >
                  Premios
                </Button>
              </div>
            </div>
          </div>
          <footer className="justify-center flex lg:space-x-[99px] lg:mb-12 lg:mt-14 my-6">
            <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
            <AdSlot divId={ADS_DIV_ID.caja2} variant="BOX" />
            <AdSlot divId={ADS_DIV_ID.caja3} variant="BOX" />
          </footer>
        </div>
      </Container>

      <Modal
        controlledOnClose={() => setShowModalAward(false)}
        isControlledOpen={showModalAward}
        render={() => (
          <div className="flex flex-col">
            <ul className="text-base list-disc space-y-2 mt-8">
              <li>
                Aquellos Participantes que lleguen a 12 medallas: Serán
                acreedores de un PIN para utilizar en Fútbol 5 de Gran DT Torneo{' '}
                {userInitialData?.torneo}.
              </li>
              <li>
                Aquellos Participantes que lleguen a 20 medallas: Serán
                acreedores de un PIN para utilizar en el equipo de Primera de
                Gran DT Torneo {userInitialData?.torneo}.
              </li>
              <li>
                Aquellos Participantes que lleguen a 40 medallas: Serán
                acreedores de un Pack Premium para lo que resta de la edición de
                Gran DT Torneo {userInitialData?.torneo}.
              </li>
              <li>
                Los 5 (cinco) usuarios que más medallas sumen al finalizar la
                edición de Gran DT Torneo {userInitialData?.torneo} ganarán un
                Smart TV cada uno.
              </li>
            </ul>
            <div className="flex justify-center mt-8">
              <Button
                className="font-bold"
                type="button"
                variant="text"
                onClick={() => {
                  setShowModalAward(false);
                  setShowModalMedals(true);
                }}
              >
                ¿Cómo sumo medallas?
              </Button>
            </div>
          </div>
        )}
        title={<ModalTitle title="Premios" />}
        variant="controlled"
      />
      <Modal
        controlledOnClose={() => setShowModalMedals(false)}
        isControlledOpen={showModalMedals}
        render={() => (
          <p className="text-base">
            <strong>
              El Medallero premia tu rendimiento en todas las competencias del
              juego.
            </strong>{' '}
            A mejor rendimiento, tendrás mayor cantidad de medallas. Hay un
            Ranking Medallero, donde se puede ver a los participantes que tienen
            la mayor cantidad de medallas. Los participantes logran sumar
            medallas en función de su desempeño en el juego. Para conocer en
            detalle las formas de ganar medallas, visitá nuestras{' '}
            <Button
              className="font-bold text-base"
              type="button"
              variant="text"
              onClick={() =>
                navigate(ROUTES.preguntasFrecuentes, {
                  state: {
                    selectedScreen: 'TeamBuildingAndAwards',
                    accordionId: 'juego-como-sumo-medallas',
                  } as SelectedScreen,
                })
              }
            >
              Preguntas frecuentes
            </Button>
          </p>
        )}
        title={<ModalTitle title="¿Cómo sumo medallas?" />}
        variant="controlled"
      />
    </NavBar>
  );
};

export default MyMedals;
